import { useState, useEffect } from 'react';
import { Clock, Pencil } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FocusSession } from '@/types/task';

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

interface LogTimeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLog: (minutes: number) => void;
  onEdit?: (sessionId: string, minutes: number) => void;
  taskName: string;
  /** Existing sessions for this task (e.g. today) so user can edit time */
  existingSessions?: FocusSession[];
}

export function LogTimeDialog({
  open,
  onOpenChange,
  onLog,
  onEdit,
  taskName,
  existingSessions = [],
}: LogTimeDialogProps) {
  const [hours, setHours] = useState('');
  const [minutes, setMinutes] = useState('');
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const isEditing = !!editingSessionId;

  const editingSession = existingSessions.find((s) => s.id === editingSessionId);

  useEffect(() => {
    if (!open) {
      setHours('');
      setMinutes('');
      setEditingSessionId(null);
    } else if (editingSession) {
      const m = editingSession.duration_minutes;
      setHours(String(Math.floor(m / 60)));
      setMinutes(String(m % 60));
    }
  }, [open, editingSession]);

  const handleSubmit = () => {
    const totalMinutes = (parseInt(hours, 10) || 0) * 60 + (parseInt(minutes, 10) || 0);
    if (totalMinutes <= 0) return;
    if (isEditing && onEdit) {
      onEdit(editingSessionId!, totalMinutes);
      setEditingSessionId(null);
    } else {
      onLog(totalMinutes);
    }
    setHours('');
    setMinutes('');
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setHours('');
      setMinutes('');
      setEditingSessionId(null);
    }
    onOpenChange(isOpen);
  };

  const startEdit = (session: FocusSession) => {
    setEditingSessionId(session.id);
    setHours(String(Math.floor(session.duration_minutes / 60)));
    setMinutes(String(session.duration_minutes % 60));
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[360px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            {isEditing ? 'Edit Time' : 'Log Time'}
          </DialogTitle>
          <DialogDescription className="truncate">
            {isEditing
              ? 'Change the duration for this entry.'
              : `Record time spent on "${taskName}"`}
          </DialogDescription>
        </DialogHeader>

        {existingSessions.length > 0 && onEdit && !isEditing && (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Edit existing entry</p>
            <div className="flex flex-wrap gap-2">
              {existingSessions.map((session) => (
                <Button
                  key={session.id}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => startEdit(session)}
                >
                  <Pencil className="h-3.5 w-3.5" />
                  {formatDuration(session.duration_minutes)}
                </Button>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center gap-4 py-4">
          <div className="flex-1">
            <Label htmlFor="hours" className="text-xs text-muted-foreground">
              Hours
            </Label>
            <Input
              id="hours"
              type="number"
              min="0"
              max="24"
              placeholder="0"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              className="mt-1"
            />
          </div>
          <div className="flex-1">
            <Label htmlFor="minutes" className="text-xs text-muted-foreground">
              Minutes
            </Label>
            <Input
              id="minutes"
              type="number"
              min="0"
              max="59"
              placeholder="0"
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
              className="mt-1"
            />
          </div>
        </div>

        <DialogFooter>
          {isEditing && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setEditingSessionId(null);
                setHours('');
                setMinutes('');
              }}
            >
              Cancel edit
            </Button>
          )}
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Close
          </Button>
          <Button onClick={handleSubmit} disabled={!hours && !minutes}>
            {isEditing ? 'Save' : 'Log Time'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
